import type { PublishedPostDto } from "@nlc/shared-types";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";

import { SOCIAL_PLATFORM_LABELS } from "../../socialPlatformLabels";
import { formatMetric } from "../formatMetric";
import { PostThumbnail } from "./PostThumbnail";

export function TopPosts({
  averageViews,
  onSeeAllPosts,
  posts,
  rangeLabel,
}: {
  averageViews: null | number;
  onSeeAllPosts: () => void;
  posts: PublishedPostDto[];
  rangeLabel: string;
}) {
  return (
    <section className="flex flex-col gap-lg">
      <div className="flex items-center justify-between gap-md">
        <h3 className="text-lg font-semibold text-text-primary">
          Your top posts · {rangeLabel}
        </h3>
        <Button color="gray" onPress={onSeeAllPosts} variant="secondary">
          See all posts
        </Button>
      </div>
      <ul className="grid gap-lg sm:grid-cols-2 lg:grid-cols-5">
        {posts.map((post) => (
          <li
            className="flex flex-col overflow-hidden rounded-xl border border-border-secondary bg-bg-primary"
            key={post.id}
          >
            <PostThumbnail className="aspect-square w-full" post={post} />
            <div className="flex flex-col gap-sm p-lg">
              <span className="truncate text-sm font-semibold text-text-primary">
                {post.name || post.designs[0]?.name || post.brand.name}
              </span>
              <span className="text-display-xs font-semibold text-text-primary">
                {formatMetric(post.insights?.views)}
                <span className="ml-xs text-sm font-normal text-text-tertiary">
                  views
                </span>
              </span>
              <span className="truncate text-sm text-text-tertiary">
                {post.page
                  ? `${post.brand.name} · ${SOCIAL_PLATFORM_LABELS[post.page.platform]}`
                  : post.brand.name}
              </span>
              <AverageBadge
                averageViews={averageViews}
                views={post.insights?.views ?? null}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AverageBadge({
  averageViews,
  views,
}: {
  averageViews: null | number;
  views: null | number;
}) {
  if (!averageViews || views === null) return null;

  const difference = (views - averageViews) / averageViews;

  return (
    <Badge
      className="self-start"
      isRounded
      size="small"
      variant={difference >= 0 ? "success" : "error"}
    >
      {difference >= 0
        ? `+${Math.round(difference * 100)}% above average`
        : "Below average"}
    </Badge>
  );
}
